import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { AcpExportStatus } from "reedy-data/models";
import { Icon } from "~/components/icon/icon";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/button";
import { Loader } from "~/components/loading/loader";
import { downloadFromOrgFiles } from "~/integrations/firebase/storage";
import { toast } from "~/utils/toast";
import { logError } from "~/shared/services/error-reporting";
import { Text } from "~/components/text";

export const AcpExportItem = ({
  count,
  status = AcpExportStatus.REQUESTED,
  createdAt,
  storagePath,
}: {
  count?: number | null;
  status?: AcpExportStatus;
  createdAt?: Date;
  storagePath?: string | null;
}) => {
  const { t } = useTranslation("settings");
  const [isDownloading, setIsDownloading] = useState(false);

  const isInProgress =
    status === AcpExportStatus.REQUESTED ||
    status === AcpExportStatus.PROCESSING;
  const canDownload = status === AcpExportStatus.COMPLETED && !!storagePath;

  const badge = useMemo(() => {
    switch (status) {
      case AcpExportStatus.COMPLETED:
        return {
          variant: "success" as const,
          label: t("organisation.onix.misc.badge.success"),
        };
      case AcpExportStatus.ERROR:
        return {
          variant: "error" as const,
          label: t("organisation.onix.misc.badge.failed"),
        };
      default:
        return null;
    }
  }, [status, t]);

  const handleDownload = async () => {
    if (!storagePath || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFromOrgFiles(storagePath, "acp-export.jsonl");
    } catch (e) {
      logError(e as Error);
      toast.error(t("organisation.exports.acp.downloadError"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <li className="flex border-secondary-200 border rounded-xl p-3 justify-between items-center">
      <div className="flex gap-3">
        <div className="border-secondary-200 border rounded-lg size-11 flex items-center justify-center">
          <Icon name="file-down" iconClassName="w-5! h-5!" />
        </div>
        <div className="flex flex-col justify-center">
          <Text variant="label">{t("organisation.exports.acp.title")}</Text>
          <Text variant="caption" className="mt-1">
            ACP &middot; {format(createdAt || new Date(), "P")}
            {typeof count === "number" && (
              <> &middot; {t("organisation.exports.acp.books", { count })}</>
            )}
          </Text>
        </div>
      </div>
      <div className="flex gap-3 items-center">
        {isInProgress && <Loader className="size-6 mr-2 border-2" />}
        {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
        <Button
          variant="tertiary"
          size="small"
          icon={isDownloading ? "loader" : "download"}
          iconClassName={isDownloading ? "animate-spin" : undefined}
          iconPosition="right"
          onClick={canDownload ? handleDownload : undefined}
          disabled={!canDownload || isDownloading}
        >
          {t("organisation.exports.acp.download")}
        </Button>
      </div>
    </li>
  );
};
